// Exercise library - Shashank's 90-Day Fat-Loss & Muscle Plan (PDF v6,
// "train every day" 7-day rotation). Every phase uses the SAME weekly split
// (Push/Pull/Legs/Upper/Lower/Full Body/Arms & Core); only the steps target
// and how close each set is pushed to failure change across phases - see
// PHASES in data/plan.js. "range" is the target rep count/range for this
// cut - add a rep or a little weight each week, do not chase big jumps in a
// deficit. watchUrl stands in for the plan's "Watch" pill (a form-check
// video search, since the source PDF's pill links aren't machine-readable).
const watchUrl = (name) => `https://www.youtube.com/results?search_query=${encodeURIComponent(name + ' exercise form')}`
const ex = (name, sets, range, note = '') => ({ name, sets, range, note, watchUrl: watchUrl(name) })

// Every workout ends with a steady-state cardio finisher (the "Cardio after
// lifting" column in the plan's 7-day split table) - modelled as one more
// timed exercise so it shows up in the session without any UI changes.
const cardio = (label, range, note = 'Cardio finisher after lifting - pace where you can talk but not sing') => ex(label, 1, range, note)

// What kind of input logging this exercise needs, inferred from its rep
// range text - a timed hold/cardio block or a rounds-based circuit doesn't
// have a "weight x reps" to log the way a lift does.
export function getExerciseLogType(range) {
  const r = range.toLowerCase()
  if (r.includes('sec')) return { type: 'time', unit: 'sec' }
  if (r.includes('min')) return { type: 'time', unit: 'min' }
  if (r.includes('round')) return { type: 'rounds' }
  return { type: 'weight-reps' }
}

export const WORKOUTS = {
  Push: {
    label: 'Push',
    exercises: [
      ex('Bench Press', 4, '8'),
      ex('Incline DB Press', 3, '10'),
      ex('Overhead Press', 3, '10'),
      ex('Lateral Raise', 4, '15'),
      ex('Triceps Pushdown + Overhead Ext.', 3, '12 each'),
      cardio('Incline treadmill walk', '15-20 min'),
    ],
  },
  Pull: {
    label: 'Pull',
    exercises: [
      ex('Deadlift (3x5) or Pull-up (4x8)', 1, 'see note', 'Alternate week to week'),
      ex('Lat Pulldown', 4, '10'),
      ex('Barbell or Cable Row', 4, '10'),
      ex('Face Pull', 3, '15'),
      ex('DB Curl + Hammer Curl', 3, '12 each'),
      cardio('Cycle', '15-20 min'),
    ],
  },
  Legs: {
    label: 'Legs',
    exercises: [
      ex('Squat', 4, '8'),
      ex('Romanian Deadlift', 3, '10'),
      ex('Leg Press', 4, '12'),
      ex('Leg Curl + Leg Extension', 3, '12 / 15'),
      ex('Calf Raise', 4, '15'),
      cardio('Easy walk', '10 min', 'Legs already worked'),
    ],
  },
  Upper: {
    label: 'Upper',
    exercises: [
      ex('Incline Bench Press', 4, '8'),
      ex('Seated Cable Row', 4, '10'),
      ex('DB Shoulder Press', 3, '10'),
      ex('Lat Pulldown', 3, '10'),
      ex('Lateral Raise + Curl/Pushdown superset', 3, '15'),
      cardio('Cross-trainer', '15-20 min'),
    ],
  },
  Lower: {
    label: 'Lower',
    exercises: [
      ex('Front Squat', 4, '8', 'Or Hack Squat'),
      ex('Romanian Deadlift', 3, '10'),
      ex('Bulgarian Split Squat', 3, '10 / leg'),
      ex('Leg Extension + Leg Curl', 3, '15 each'),
      ex('Calf Raise + Cable Crunch', 4, '20 / 15'),
      cardio('Easy cycle', '10-15 min'),
    ],
  },
  FullBody: {
    label: 'Full Body',
    exercises: [
      ex('Goblet Squat', 3, '10'),
      ex('Machine Chest Press', 3, '10'),
      ex('Lat Pulldown', 3, '10'),
      ex('Seated DB Shoulder Press', 3, '10'),
      ex('Plank', 3, '30 sec'),
      cardio('Incline walk', '15-20 min'),
    ],
  },
  ArmsCore: {
    label: 'Arms & Core',
    exercises: [
      ex('Dumbbell Bicep Curl', 3, '12'),
      ex('Triceps Rope Pushdown', 3, '12'),
      ex('Hammer Curl', 3, '12'),
      ex('Overhead Triceps Extension', 3, '12'),
      ex('Lateral Raise', 3, '15'),
      ex('Hanging Leg Raise', 3, '15'),
      ex('Plank', 3, '45 sec'),
      ex('Russian Twist', 3, '20'),
      cardio('Steady walk or cycle', '30-40 min', 'Lighter lifting day - longer cardio'),
    ],
  },
}

// Weekly cycle (7-day repeating pattern), index 0 = Monday. Unlike the old
// plan this is the SAME rotation for every phase - you train every day, and
// each muscle still gets 48-72h to recover because the split rotates: Push
// hits Mon & Thu, Pull Tue & Fri, Legs Wed & Sat. Sunday's Arms & Core is
// the lighter day (see PHASES in data/plan.js for the deliberate "swap
// Sunday for a full rest day if you ever want to" note).
const WEEK = ['Push', 'Pull', 'Legs', 'Upper', 'Lower', 'FullBody', 'ArmsCore']
export const WEEKLY_PATTERN = {
  1: WEEK,
  2: WEEK,
  3: WEEK,
}

// Overrides WORKOUTS/WEEKLY_PATTERN in place from a Firestore-sourced plan
// (see lib/hooks.js's usePlanLibrary and data/plan.js's registerPlanLibrary)
// - mutates the existing exported objects rather than replacing them, same
// mutate-in-place pattern data/foods.js uses for registerDishes(), so every
// module that already imported WORKOUTS/WEEKLY_PATTERN sees the update
// without re-importing anything. The bundled values above are the fallback
// used until (or unless) Firestore has this data.
export function registerWorkoutLibrary({ workouts, weeklyPattern }) {
  Object.assign(WORKOUTS, workouts)
  Object.assign(WEEKLY_PATTERN, weeklyPattern)
}
